(ns dre.pages.detail.events
  (:require
   [clojure.set :as set]
   [day8.re-frame.tracing :refer-macros [fn-traced]]
   [dre.app.router :as router :refer [->route]]
   [dre.coll :refer [nn-hash-map]]
   [dre.components.search-results.events :as search-events]
   [dre.pages.detail.utils :refer [transform-article-annotation plot-data]]
   [dre.network :as-alias network] 
   [dre.utils :refer [stringify-keys-with-namespaces]]
   [re-frame.core :as rf]
   [superstructor.re-frame.fetch-fx :as-alias fetch-fx]))

(def db-path :detail)

(def pico-states [:outline :highlight :none])

(def tracked-medcodes-vocabs
  #{"ICD9CM" "ICD10" "RXNORM" "NCI"})

(def medcodes-vocab->label
  {"ICD9CM" "ICD-9"
   "ICD10"  "ICD-10"
   "RXNORM" "RxNorm"
   "NCI"    "NCI"})

(def medcodes-xform
  (comp
    ;; extract codes for each sense
   (map (juxt :sense/id :sense/codes))
    ;; filter out codes from unwanted vocabularies
   (map (fn [[sense codes]]
          [sense (filter #(tracked-medcodes-vocabs (:term/vocab %)) codes)]))
    ;; filter out senses with zero codes
   (filter (comp seq second))
    ;; turn codes vector to vocab->codes hashmap
   (map (fn [[sense codes]]
          [sense (reduce
                  (fn [acc {:keys [:term/vocab :term/code]}]
                    (update acc vocab conj code))
                  {}
                  codes)]))))

;; -- Events

(rf/reg-event-fx
 ::init
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [new-route]]
  (let [guid (get-in new-route [:route-params :id])
        related-base (-> new-route :query-params :related-base)]
    {:db (merge (select-keys db [:prev-next])
                {:guid guid
                 :data {:beeswarm {:show-lines? true
                                   :optimistic-guid nil}
                        :selected-ds-tags #{}
                        :selected-medcodes #{}
                        :pico {:intervention [:outline :highlight :none]
                               :outcome [:outline :highlight :none]
                               :characteristic [:outline :highlight :none]
                               :misc [:none :outline :highlight]}}})
     :dispatch-n [[::fetch-article guid related-base]
                  [::fetch-article-recommendations guid]]})))

;; prev-next 
;; optimistic-prev-next
(rf/reg-event-fx
 ::optimistic-prev-next
 [rf/trim-v]
 (fn-traced
  [{db :db} _]
  (let [search-results-db (get db search-events/db-path)
        idx (get-in search-results-db [:clicked-article :idx])
        search-params (get-in search-results-db [:clicked-article :search-params])
        search-params (assoc search-params
                             :offset (max 0 (- idx 2))
                             :limit 3)]
    (when idx
      {:db (assoc-in db [db-path :prev-next] {:idx idx
                                              :search-params search-params
                                              :loading? true
                                              :total 0
                                              :cursor [{} {}]})
       ::network/request [{:endpoint :search/articles}
                          {:body (stringify-keys-with-namespaces search-params)
                           :on-success [::prev-next-ok]}]}))))

(rf/reg-event-fx
 ::prev-next-ok
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [{:keys [::fetch-fx/response]}]]
  (let [[a1 a2 a3] (:articles (:body response))
        total (:total (:body response))
        idx (get-in db [:prev-next  :idx])
        prev (if (= idx 1) {} a1)
        next (if (= idx 1) a2 a3)]
    {:db (-> db
             (assoc-in [:prev-next :total] total)
             (assoc-in [:prev-next :cursor] [prev next])
             (assoc-in [:prev-next :loading?] false))})))

;; article

(rf/reg-event-fx
 ::fetch-article
 [rf/trim-v]
 (fn-traced
  [{db :db} [guid related-base]]
  {:db (assoc-in db [db-path :state :article] :loading)
   ::network/request [{:endpoint :detail/articles-old-detail
                       :context {:id guid}}
                      {:params (nn-hash-map
                                :relations true
                                :related-base related-base
                                :annotate-xml true
                                :skip-classification 
                                (-> db
                                    :app 
                                    :skip-classification))
                       :on-success [::success-fetch {:related-base related-base}]
                       :on-failure [::failure-fetch]}]}))

(rf/reg-event-fx
 ::success-fetch
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [{:keys [::fetch-fx/response related-base]}]]
  (let [transformed-response (transform-article-annotation (:body response))
        article (:article transformed-response)
        article (assoc article
                       :related-base
                       related-base)
        tag-articles {:articles [{:source (:article/source article)
                                  :uri (:article/uri article)
                                  :tags (map #(update % :tag_uuid str)
                                             (:article/utags article))}]}
        article-senses (apply set/union
                              (map :senses
                                   (get-in article [:xml-annotation :highlights])))]
    {:db (-> db
           (assoc-in [:state :article] :success)
           (assoc-in [:data :article] article))
     :dispatch-n [[::search-events/good-utags tag-articles]
                  [::fetch-oa-doi article]
                  [::fetch-medcodes article-senses]]})))

(rf/reg-event-fx
 ::failure-fetch
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [{:keys [::fetch-fx/response] :as opts}]]
  {:db (-> db
           (assoc-in [:state :article] :failure))
   :dispatch-n [[:dre.network/default-failure opts]]}))

;; recommendations

(rf/reg-event-fx
 ::fetch-article-recommendations
 [rf/trim-v]
 (fn-traced
  [{db :db} [guid]]
  {:db (-> db
           (assoc-in [db-path :state :recommendations] :loading)
           (assoc-in [db-path :data :recommendations] []))
   ::network/request [{:endpoint :detail/recommendations
                       :context {:id guid
                                 :k 20}
                       :request-timeout-s 60}
                      {:on-success [::success-fetch-recommendations]
                       :on-failure [::failure-fetch-recommendations]}]}))

(rf/reg-event-fx
 ::success-fetch-recommendations
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [{:keys [::fetch-fx/response]}]]
  (let [results (:article/ids (:body response))]
    {:db (-> db
             (assoc-in [:state :recommendations] :success)
             (assoc-in [:data :recommendations] results))})))

(rf/reg-event-fx
 ::failure-fetch-recommendations
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [{:keys [::fetch-fx/response] :as opts}]]
  {:db (-> db
           (assoc-in [:state :recommendations] :failure))
   :dispatch-n [[:dre.network/default-failure opts]]}))

;; oa-doi

(rf/reg-event-fx
 ::fetch-oa-doi
 [rf/trim-v]
 (fn-traced
  [{db :db} [article]]
  (when-let [doi (get-in article [:article/meta :doi])]
    {:db (assoc-in db [:data :oa-url] {})
     ::network/request [{:endpoint :detail/oa-doi}
                        {:params {:doi doi
                                  "response/filter"
                                  (.stringify js/JSON
                                              (clj->js {:crossref {:url true}
                                                        :oadoi {:url true :meta {:is_oa true}}}))}
                         :on-success [::success-oa-doi-fetch]}]})))

(rf/reg-event-fx
 ::success-oa-doi-fetch
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [{:keys [::fetch-fx/response]}]]
  (if-let [oa-url (:body response)]
    {:db (assoc-in db [:data :oa-url] oa-url)}
    {:db (assoc-in db [:data :oa-url] {})})))

;; medcodes

(rf/reg-event-fx
 ::fetch-medcodes
 [rf/trim-v]
 (fn-traced
  [{db :db} [senses]]
  {:db (assoc-in db [:data :oa-url] {})
   ::network/request [{:endpoint :dictionary/senses}
                      {:body (stringify-keys-with-namespaces {:dict/senses senses
                                                              :dict/codes? true})
                       :on-success [::success-fetch-medcodes]}]}))

(rf/reg-event-fx
 ::success-fetch-medcodes
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [{:keys [::fetch-fx/response]}]]
  (let [medcodes-lookup (into {} medcodes-xform (:dict/sense-details (:body response)))]
    {:db (assoc-in db [:data :medcodes] medcodes-lookup)})))

(rf/reg-event-fx
 ::toggle-medcodes
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [key]]
  (letfn [(update-fn [vocabs]
            (if (contains? vocabs key)
              (disj vocabs key)
              (conj vocabs key)))]
    {:db (update-in db [:data :selected-medcodes] update-fn)})))

;;pico

(rf/reg-event-fx
 ::cycle-pico
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [key]]
  (let [v (get-in db [:data :pico key])
        v (if (= (count v) 1)
            (concat v pico-states)
            v)]
    {:db (assoc-in db [:data :pico key]
                   (rest v))})))

(rf/reg-event-fx
 ::dim-picos
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} _]
  (let [update-fn (fn [old-state]
                    (if (= (first old-state) :highlight)
                      pico-states
                      old-state))]
    {:db (-> db
             (update-in [:data :pico :intervention] update-fn)
             (update-in [:data :pico :outcome] update-fn)
             (update-in [:data :pico :characteristic] update-fn)
             (update-in [:data :pico :misc] update-fn))})))

;;ds tags

(rf/reg-event-fx
 ::toggle-ds-tag
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [key]]
  (letfn [(update-fn [tags]
                   (if (contains? tags key)
                     (disj tags key)
                     (conj tags key)))]
    {:db (update-in db [:data :selected-ds-tags] update-fn)})))

;;beeswarm

(rf/reg-event-fx
 ::toggle-beeswarm-lines
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [_]]
  {:db (update-in db [:data :beeswarm :show-lines?] not)}))

(rf/reg-event-fx
 ::update-beeswarm-plot
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [current-guid related-base new-guid new-group]]
  (when (not= current-guid new-guid)
    {:dispatch [::router/redirect-to-path-with-query-params (->route :detail {:id new-guid}) (when (not= new-guid related-base)
                                                                                               {:related-base
                                                                                                related-base})]})))

;; -- Subscriptions

(rf/reg-sub
 ::state
 (fn [db _]
   (db-path db)))

(rf/reg-sub
 ::loading?
 :<- [::state]
 (fn [state _]
   (let [article? (= (get-in state [:state :article]) :loading)]
     article?)))

(rf/reg-sub
 ::loading-recommendations?
 :<- [::state]
 (fn [state _]
   (let [loading? (= (get-in state [:state :recommendations]) :loading)]
     loading?)))

(rf/reg-sub
 ::data
 :<- [::state]
 (fn [state _]
   (get-in state [:data])))

(rf/reg-sub
 ::prev-next
 :<- [::state]
 (fn [state _]
   (get-in state [:prev-next])))


(rf/reg-sub
 ::pico-classes
 :<- [::state]
 (fn [state _]
   (map (fn [[k v]]
          (str (name k) "-" (name (first v))))
        (get-in state [:data :pico]))))

(rf/reg-sub
 ::pico-class
 :<- [::state]
 (fn [state [_ key]]
   (first (get-in state [:data :pico key]))))

(rf/reg-sub
 ::selected-ds-tags
 :<- [::state]
 (fn [state _]
   (get-in state [:data :selected-ds-tags])))

(rf/reg-sub
 ::medcodes
 :<- [::state]
 (fn [state _]
   (get-in state [:data :medcodes])))

(rf/reg-sub
 ::selected-medcodes
 :<- [::state]
 (fn [state _]
   (get-in state [:data :selected-medcodes])))

(rf/reg-sub
 ::beeswarm-optimistic-guid
 :<- [::state]
 (fn [state _]
   (get-in state [:data :beeswarm :optimistic-guid])))

(rf/reg-sub
 ::beeswarm-show-lines
 :<- [::state]
 (fn [state _]
   (get-in state [:data :beeswarm :show-lines?])))

(rf/reg-sub
 ::beeswarm-plot
 :<- [::state]
 :<- [::beeswarm-optimistic-guid]
 :<- [::beeswarm-show-lines]
 (fn [[state optimistic-guid show-lines?] [_ format-related-uri]]
   (let [beeswarm-data (plot-data (get-in state [:data :article]) format-related-uri ::update-beeswarm-plot)]
     (if optimistic-guid
       (-> beeswarm-data
           (update :data
                   (fn [data]
                     (let [icon
                           (if (= optimistic-guid
                                  (:related-base beeswarm-data))
                             :thumbtack
                             :location-dot)
                           mf (fn [rel]
                                (cond-> rel (= optimistic-guid
                                               (:target/guid rel))
                                        (assoc :beeswarm/icon icon)
                                        (= :location-dot (:beeswarm/icon rel))
                                        (dissoc :beeswarm/icon)))
                           data (map mf data)]
                       data)))
           (assoc :click-handler
                  (fn [d]
                    (let [new-guid (:target/guid d)
                          group (:target/group d)]
                      (rf/dispatch [::update-beeswarm-plot
                                    optimistic-guid
                                    (:related-base beeswarm-data)
                                    new-guid
                                    group]))))
           (assoc :show-lines? show-lines?))
       (assoc beeswarm-data
              :show-lines? show-lines?)))))